import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Avatar,
  Button,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Support,
  Assignment,
  CheckCircle,
  Schedule,
  Error,
  Add,
  Refresh,
  Visibility,
  Person,
  Search,
  FilterList,
  Clear,
  ExpandMore,
  ExpandLess,
  Edit,
  LocationOn,
  List as ListIcon,
  CalendarMonth,
  EmojiEvents,
  HeadsetMic,
  Label as LabelIcon,
} from '@mui/icons-material';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { format, parse, startOfWeek, getDay, lastDayOfMonth, getDate } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { ticketService } from '../services/ticketService';
import { chamadoAssuntoService, ChamadoAssunto } from '../services/chamadoAssuntoService';
import { Ticket } from '../types';
import { toast } from 'react-toastify';
import ChamadoForm from '../components/forms/ChamadoForm';
import ChamadosInfoView from '../components/modals/ChamadosInfoView';
import ChamadoAssuntoSelect from '../components/selects/ChamadoAssuntoSelect';
import FuncionariosSelect from '../components/selects/FuncionariosSelect';
import UsuariosSelect from '../components/selects/UsuariosSelect';
import SysDateRangePicker from '../components/SysDateRangePicker';
import { useRelatorioCores } from './relatorios';
import {
  themedStatCardSx,
  themedStatIconBoxSx,
  filterBarHeaderSx,
  getBigCalendarStyles,
} from '../styles/theme';
import ChamadosRankingTab from './ChamadosRankingTab';
import ChamadosRankingTecnicosTab from './ChamadosRankingTecnicosTab';
import ChamadosRankingAssuntosTab from './ChamadosRankingAssuntosTab';

const calendarLocales = { 'pt-BR': ptBR };

const calendarLocalizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date: Date) => startOfWeek(date, { weekStartsOn: 1 }),
  getDay,
  locales: calendarLocales,
});

interface ChamadoCalendarEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  resource: Ticket;
}

const DnDCalendar = withDragAndDrop<ChamadoCalendarEvent>(Calendar as any);

const ChamadosPage: React.FC = () => {
  const theme = useTheme();
  const cores = useRelatorioCores();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeTab, setActiveTab] = useState(0);

  // Estados do calendário - inicia em semana atual conforme o sistema de referência
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState<any>(Views.WEEK);

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [infoViewOpen, setInfoViewOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [editTicketId, setEditTicketId] = useState<number | null>(null);
  const [stats, setStats] = useState({
    aberto: 0,
    em_andamento: 0,
    resolvido: 0,
    urgente: 0,
    total: 0,
  });

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('ativos'); // 'ativos' = padrão, 'todos' = todos incluindo cancelado/fechado
  const [filterPrioridade, setFilterPrioridade] = useState<string>('todos');
  const [filterAssunto, setFilterAssunto] = useState<number | null>(null);
  const [filterFuncionario, setFilterFuncionario] = useState<number | null>(null);
  const [filterAbertoPor, setFilterAbertoPor] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
    startDate: null,
    endDate: null,
  });
  const [filtersExpanded, setFiltersExpanded] = useState(true);


  const loadTickets = async () => {
    try {
      setLoading(true);
      setError(null);

      // Construir filtros para o backend
      const filters: any = {};

      // Enviar status apenas se for 'todos' (para retornar todos incluindo cancelado/fechado)
      // Se for 'ativos' ou outro valor, não enviar e o backend excluirá cancelado/fechado por padrão
      if (filterStatus === 'todos') {
        filters.status = 'todos';
      } else if (filterStatus !== 'ativos') {
        // Status específico selecionado
        filters.status = filterStatus;
      }
      // Se filterStatus === 'ativos', não enviar nada e o backend usa o padrão

      if (filterPrioridade !== 'todos') {
        filters.prioridade = filterPrioridade;
      }

      if (filterAssunto !== null) {
        filters.chamado_assunto_id = filterAssunto;
      }

      if (filterFuncionario !== null) {
        filters.atribuido_para = filterFuncionario;
      }

      if (filterAbertoPor !== null) {
        filters.aberto_por = filterAbertoPor;
      }

      if (searchTerm && searchTerm.trim()) {
        filters.search = searchTerm.trim();
      }

      if (dateRange.startDate) {
        filters.data_inicio = dateRange.startDate.toISOString().split('T')[0];
      }

      if (dateRange.endDate) {
        filters.data_fim = dateRange.endDate.toISOString().split('T')[0];
      }

      const response = await ticketService.getAll({
        limit: 1000,
        filters
      });

      if (response.success && response.data) {
        const allTickets = response.data;
        setTickets(allTickets);

        // Calcular estatísticas
        setStats({
          aberto: allTickets.filter(t => t.status === 'aberto').length,
          em_andamento: allTickets.filter(t => t.status === 'em_andamento').length,
          resolvido: allTickets.filter(t => t.status === 'resolvido').length,
          urgente: allTickets.filter(t => t.prioridade === 'urgente' && ['aberto', 'em_andamento'].includes(t.status)).length,
          total: allTickets.length,
        });
      } else {
        setError(response.message || 'Erro ao carregar chamados');
      }
    } catch (error: any) {
      console.error('Erro ao carregar chamados:', error);
      setError('Erro ao carregar chamados');
      toast.error('Erro ao carregar chamados');
    } finally {
      setLoading(false);
    }
  };

  // Ref para controlar se é a primeira renderização
  const isFirstRender = useRef(true);

  // Debounce para o campo de busca
  useEffect(() => {
    // Não buscar na primeira renderização (será feito no useEffect principal)
    if (isFirstRender.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      loadTickets();
    }, 500); // 500ms de delay

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Buscar quando outros filtros mudarem (exceto busca que tem debounce próprio)
  useEffect(() => {
    // Não buscar na primeira renderização (será feito no useEffect principal)
    if (isFirstRender.current) {
      return;
    }

    loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterPrioridade, filterAssunto, filterFuncionario, filterAbertoPor, dateRange.startDate, dateRange.endDate]);

  // Buscar ao montar o componente (apenas uma vez)
  useEffect(() => {
    loadTickets();
    isFirstRender.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNewTicket = () => {
    setEditTicketId(null);
    setFormOpen(true);
  };

  const handleFormSuccess = () => {
    loadTickets(); // Recarregar tickets após criar novo chamado
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'aberto':
        return 'error';
      case 'em_andamento':
        return 'warning';
      case 'resolvido':
        return 'success';
      case 'fechado':
        return 'default';
      case 'cancelado':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      aberto: 'Aberto',
      em_andamento: 'Em Andamento',
      resolvido: 'Resolvido',
      fechado: 'Fechado',
      cancelado: 'Cancelado',
    };
    return labels[status] || status;
  };

  const getPrioridadeLabel = (prioridade: string) => {
    const labels: Record<string, string> = {
      baixa: 'Baixa',
      normal: 'Normal',
      alta: 'Alta',
      urgente: 'Urgente',
    };
    return labels[prioridade] || prioridade;
  };

  const getPrioridadeColor = (prioridade: string) => {
    switch (prioridade) {
      case 'baixa':
        return 'success';
      case 'normal':
        return 'info';
      case 'alta':
        return 'warning';
      case 'urgente':
        return 'error';
      default:
        return 'default';
    }
  };

  // Os tickets já vêm filtrados do backend, então não precisamos filtrar localmente
  // Apenas ordenamos se necessário
  const filteredTickets = useMemo(() => {
    // Ordenar por data de criação (mais recentes primeiro)
    return [...tickets].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [tickets]);

  // Eventos do calendário: chamados com data_agendamento
  const calendarEvents: ChamadoCalendarEvent[] = useMemo(() => {
    return tickets
      .filter((t) => !!t.data_agendamento)
      .map((t) => {
        const start = new Date(t.data_agendamento!);
        const end = new Date(start);
        end.setHours(end.getHours() + 1);
        const startTime = format(start, 'HH:mm');
        const clienteNome = t.cliente?.nome || 'Sem cliente';
        const assuntoNome = t.assunto?.nome || 'Sem assunto';
        return {
          id: t.id,
          title: `${startTime} ${clienteNome} — ${assuntoNome}`,
          start,
          end,
          resource: t,
        };
      });
  }, [tickets]);

  const getCalendarEventColor = (event: ChamadoCalendarEvent) => {
    const { status, prioridade } = event.resource;
    if (prioridade === 'urgente') return theme.palette.error.main;
    if (status === 'resolvido' || status === 'fechado') return theme.palette.success.main;
    if (status === 'em_andamento') return theme.palette.info.main;
    if (status === 'aberto') return theme.palette.warning.main;
    return theme.palette.grey[500];
  };

  const calendarEventStyleGetter = (event: ChamadoCalendarEvent) => {
    const backgroundColor = getCalendarEventColor(event);
    if (calendarView === Views.AGENDA) {
      return {
        style: {
          borderLeft: `5px solid ${backgroundColor}`,
          backgroundColor: alpha(backgroundColor, 0.07),
          color: theme.palette.text.primary,
        },
      };
    }
    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: 'none',
        display: 'block',
        fontSize: '0.8rem',
        padding: '2px 5px',
      },
    };
  };

  const formatAgendaDateLabel = (date: Date, label: string): { text: string; color: string } => {
    try {
      if (!date || isNaN(date.getTime())) return { text: label || '---', color: theme.palette.text.secondary };
      const dateStr = format(date, 'yyyy-MM-dd');
      const todayStr = format(new Date(), 'yyyy-MM-dd');
      const yesterdayStr = format(new Date(new Date().setDate(new Date().getDate() - 1)), 'yyyy-MM-dd');
      const tomorrowStr = format(new Date(new Date().setDate(new Date().getDate() + 1)), 'yyyy-MM-dd');
      if (dateStr === todayStr) return { text: 'Hoje', color: theme.palette.primary.main };
      if (dateStr === yesterdayStr) return { text: 'Ontem', color: theme.palette.error.main };
      if (dateStr === tomorrowStr) return { text: 'Amanhã', color: theme.palette.success.main };
      return { text: format(date, "EEE, dd 'de' MMM", { locale: ptBR }), color: theme.palette.text.secondary };
    } catch {
      return { text: label || '---', color: theme.palette.text.secondary };
    }
  };

  // Horários de exibição da grade semanal (06:00 às 22:00)
  const minCalendarTime = useMemo(() => {
    const d = new Date();
    d.setHours(6, 0, 0, 0);
    return d;
  }, []);

  const maxCalendarTime = useMemo(() => {
    const d = new Date();
    d.setHours(22, 0, 0, 0);
    return d;
  }, []);

  // Formatações customizadas: dias da semana por extenso (Segunda-Feira 12, Terça-Feira 13, etc.)
  const calendarFormats = useMemo(
    () => ({
      dayFormat: (date: Date) => {
        const raw = format(date, 'EEEE', { locale: ptBR });
        const capitalized = raw
          .split('-')
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join('-');
        const dayNum = format(date, 'dd');
        return `${capitalized} ${dayNum}`;
      },
      weekdayFormat: (date: Date) => {
        const raw = format(date, 'EEEE', { locale: ptBR });
        return raw
          .split('-')
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join('-');
      },
      dayHeaderFormat: (date: Date) => {
        const raw = format(date, 'EEEE', { locale: ptBR });
        const capitalized = raw
          .split('-')
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join('-');
        return `${capitalized}, ${format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}`;
      },
    }),
    []
  );

  // Manipulador de Drag-and-Drop: arrastar chamado para outro dia ou horário
  const handleEventDrop = async ({ event, start }: any) => {
    const newDate = new Date(start);
    const ticketId = (event as ChamadoCalendarEvent).resource?.id || (event as ChamadoCalendarEvent).id;

    // Salvar estado prévio para rollback se ocorrer erro
    const previousTickets = [...tickets];

    // Atualização otimista imediata na interface
    setTickets((prev) =>
      prev.map((t) => (t.id === ticketId ? { ...t, data_agendamento: newDate.toISOString() } : t))
    );

    try {
      const res = await ticketService.updateSchedule(ticketId, newDate);
      if (res.success) {
        toast.success(
          `Agendamento do chamado #${ticketId.toString().padStart(5, '0')} alterado para ${format(
            newDate,
            "dd/MM/yyyy 'às' HH:mm",
            { locale: ptBR }
          )}`
        );
      } else {
        setTickets(previousTickets);
        toast.error(res.message || 'Erro ao alterar agendamento do chamado');
      }
    } catch (err: any) {
      setTickets(previousTickets);
      toast.error('Erro ao salvar alteração de agendamento');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('todos');
    setFilterPrioridade('todos');
    setFilterAssunto(null);
    setFilterFuncionario(null);
    setFilterAbertoPor(null);
    setDateRange({ startDate: null, endDate: null });
  };

  const hasActiveFilters =
    searchTerm ||
    filterStatus !== 'todos' ||
    filterPrioridade !== 'todos' ||
    filterAssunto !== null ||
    filterFuncionario !== null ||
    filterAbertoPor !== null ||
    dateRange.startDate !== null ||
    dateRange.endDate !== null;

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 60) {
        return `Há ${diffMins}min`;
      } else if (diffHours < 24) {
        return `Há ${diffHours}h`;
      } else if (diffDays === 1) {
        return 'Há 1 dia';
      } else {
        return `Há ${diffDays} dias`;
      }
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ p: 0.5 }}>
      <Paper
        elevation={2}
        sx={{
          mb: 1,
          borderRadius: 2,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(cores.primario, 0.04)} 0%, ${alpha(cores.secundario, 0.04)} 100%)`,
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(cores.primario, 0.12), color: cores.primario, width: 40, height: 40 }}>
              <Support sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                Sistema de Chamados
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                Dashboard e gerenciamento de chamados
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Tooltip title="Atualizar dados">
              <IconButton
                size="small"
                onClick={loadTickets}
                disabled={loading}
                sx={{ border: `1px solid ${theme.palette.divider}` }}
              >
                {loading ? <CircularProgress size={18} /> : <Refresh sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant="contained"
              startIcon={<Add sx={{ fontSize: 16 }} />}
              onClick={handleNewTicket}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.78rem',
                borderRadius: 1.5,
                boxShadow: 'none',
                '&:hover': { boxShadow: theme.shadows[2] },
              }}
            >
              Novo Chamado
            </Button>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 1, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Resumo de Chamados */}
      {activeTab < 2 && (
        <Grid container spacing={1.5} mb={1}>
          <Grid item xs={6} sm={6} md={3}>
            <Card elevation={0} sx={themedStatCardSx(theme, 'warning')}>
              <CardContent sx={{ p: 2.5 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Abertos
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="warning.main" sx={{ mt: 0.5 }}>
                      {loading ? '...' : stats.aberto}
                    </Typography>
                  </Box>
                  <Box sx={themedStatIconBoxSx(theme, 'warning')}>
                    <Assignment sx={{ fontSize: 28, color: 'warning.main' }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card elevation={0} sx={themedStatCardSx(theme, 'info')}>
              <CardContent sx={{ p: 2.5 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Em Andamento
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="info.main" sx={{ mt: 0.5 }}>
                      {loading ? '...' : stats.em_andamento}
                    </Typography>
                  </Box>
                  <Box sx={themedStatIconBoxSx(theme, 'info')}>
                    <Schedule sx={{ fontSize: 28, color: 'info.main' }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card elevation={0} sx={themedStatCardSx(theme, 'success')}>
              <CardContent sx={{ p: 2.5 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Resolvidos
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="success.main" sx={{ mt: 0.5 }}>
                      {loading ? '...' : stats.resolvido}
                    </Typography>
                  </Box>
                  <Box sx={themedStatIconBoxSx(theme, 'success')}>
                    <CheckCircle sx={{ fontSize: 28, color: 'success.main' }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card elevation={0} sx={themedStatCardSx(theme, 'error')}>
              <CardContent sx={{ p: 2.5 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Urgentes
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="error.main" sx={{ mt: 0.5 }}>
                      {loading ? '...' : stats.urgente}
                    </Typography>
                  </Box>
                  <Box sx={themedStatIconBoxSx(theme, 'error')}>
                    <Error sx={{ fontSize: 28, color: 'error.main' }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Abas: Listar / Agenda / Rankings */}
      <Paper
        elevation={0}
        sx={{
          mb: 1,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          sx={{
            minHeight: 40,
            px: 1,
            '& .MuiTab-root': {
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.82rem',
              py: 0,
            },
          }}
          TabIndicatorProps={{ style: { height: 3, borderRadius: '3px 3px 0 0' } }}
        >
          <Tab icon={<ListIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Listar" />
          <Tab icon={<CalendarMonth sx={{ fontSize: 16 }} />} iconPosition="start" label="Agenda" />
          <Tab icon={<EmojiEvents sx={{ fontSize: 16 }} />} iconPosition="start" label="Ranking por Clientes" />
          <Tab icon={<HeadsetMic sx={{ fontSize: 16 }} />} iconPosition="start" label="Ranking por Técnicos" />
          <Tab icon={<LabelIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Ranking por Assuntos" />
        </Tabs>
      </Paper>

      {/* Filtros — visível apenas na aba Listar */}
      {activeTab === 0 && <Paper
        elevation={0}
        sx={{
          mb: 1,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <Box sx={filterBarHeaderSx(theme)} onClick={() => setFiltersExpanded(!filtersExpanded)}>
          <Box display="flex" alignItems="center" gap={1}>
            <FilterList fontSize="small" color="action" />
            <Typography variant="subtitle2" fontWeight={600}>
              Filtros
            </Typography>
            {hasActiveFilters && (
              <Chip
                label={`${filteredTickets.length} resultado(s)`}
                size="small"
                color="primary"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
          <IconButton size="small">
            {filtersExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={filtersExpanded}>
          <Box sx={{ p: 1 }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={4} md={2}>
                <ChamadoAssuntoSelect
                  value={filterAssunto}
                  onChange={(id) => setFilterAssunto(id)}
                  label="Assunto"
                  placeholder="Todos"
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4} md={1.5}>
                <FuncionariosSelect
                  value={filterFuncionario}
                  onChange={(id) => setFilterFuncionario(id)}
                  label="Técnico"
                  placeholder="Todos"
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4} md={1.5}>
                <UsuariosSelect
                  value={filterAbertoPor}
                  onChange={(id) => setFilterAbertoPor(id)}
                  label="Aberto por"
                  placeholder="Todos"
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Buscar por cliente, ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchTerm('')}
                          edge="end"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4} md={1.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    label="Status"
                    onChange={(e) => setFilterStatus(e.target.value)}
                    sx={{ borderRadius: 2 }}
                    size="small"
                  >
                    <MenuItem value="ativos">Ativos (Padrão)</MenuItem>
                    <MenuItem value="todos">Todos os Status</MenuItem>
                    <MenuItem value="aberto">Aberto</MenuItem>
                    <MenuItem value="em_andamento">Em Andamento</MenuItem>
                    <MenuItem value="resolvido">Resolvido</MenuItem>
                    <MenuItem value="fechado">Fechado</MenuItem>
                    <MenuItem value="cancelado">Cancelado</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Prioridade</InputLabel>
                  <Select
                    value={filterPrioridade}
                    label="Prioridade"
                    onChange={(e) => setFilterPrioridade(e.target.value)}
                    sx={{ borderRadius: 2 }}
                    size="small"
                  >
                    <MenuItem value="todos">Todas as Prioridades</MenuItem>
                    <MenuItem value="baixa">Baixa</MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="alta">Alta</MenuItem>
                    <MenuItem value="urgente">Urgente</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={2.5}>
                <Box sx={{
                  '& .sys-date-range-picker input': {
                    fontSize: '0.875rem',
                    padding: '8.5px 14px',
                    borderRadius: '16px',
                  }
                }}>
                  <SysDateRangePicker
                    value={dateRange}
                    onChange={(range) => setDateRange(range)}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={1}>
                {hasActiveFilters && (
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Clear />}
                    onClick={clearFilters}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                    }}
                  >
                    Limpar
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>}

      {/* ===== ABA LISTAR ===== */}
      {activeTab === 0 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
            <Typography variant="h6" fontWeight={700}>
              Chamados
              {hasActiveFilters && (
                <Chip
                  label={`${filteredTickets.length} de ${tickets.length}`}
                  size="small"
                  sx={{ ml: 1.5, height: 24 }}
                />
              )}
            </Typography>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                Carregando chamados...
              </Typography>
            </Box>
          ) : filteredTickets.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 6,
                textAlign: 'center',
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider',
              }}
            >
              <Support sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {hasActiveFilters ? 'Nenhum chamado encontrado' : 'Nenhum chamado cadastrado'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {hasActiveFilters
                  ? 'Tente ajustar os filtros para encontrar mais resultados'
                  : 'Clique em "Novo Chamado" para criar o primeiro chamado'
                }
              </Typography>
              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  startIcon={<Clear />}
                  onClick={clearFilters}
                  sx={{ mt: 3, borderRadius: 2, textTransform: 'none' }}
                >
                  Limpar Filtros
                </Button>
              )}
            </Paper>
          ) : (
            <TableContainer
              component={Paper}
              elevation={2}
              sx={{
                borderRadius: 2,
                overflowX: 'auto',
                mb: 3,
                border: 'none'
              }}
            >
              <Table
                size="small"
                stickyHeader
                sx={{
                  minWidth: 800,
                  '& .MuiTableBody-root .MuiTableCell-root': {
                    py: 0.6,
                    fontSize: '0.78rem'
                  }
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      '& .MuiTableCell-head': {
                        bgcolor: (t) => `color-mix(in srgb, ${theme.palette.primary.main} 12%, ${t.palette.background.paper})`,
                        fontWeight: 700,
                        fontSize: '0.73rem',
                        py: 0.8,
                        nowrap: 'nowrap',
                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        whiteSpace: 'nowrap',
                        color: 'text.primary',
                      },
                    }}
                  >
                    <TableCell sx={{ width: 80 }}>ID</TableCell>
                    <TableCell>Cliente / Solicitante</TableCell>
                    <TableCell>Assunto</TableCell>
                    <TableCell sx={{ width: 100 }}>Prioridade</TableCell>
                    <TableCell sx={{ width: 100 }}>Status</TableCell>
                    <TableCell>Técnico</TableCell>
                    <TableCell>Abertura</TableCell>
                    <TableCell>Agendamento</TableCell>
                    <TableCell align="right" sx={{ width: 80 }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTickets.map((ticket) => {
                    const statusColor = getStatusColor(ticket.status);
                    const prioridadeColor = getPrioridadeColor(ticket.prioridade);

                    return (
                      <TableRow
                        key={ticket.id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          transition: 'background 0.15s',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                          nowrap: 'nowrap',
                        }}
                        onClick={() => {
                          setSelectedTicketId(ticket.id);
                          setInfoViewOpen(true);
                        }}
                      >
                        <TableCell sx={{ py: 0.6, px: 2 }}>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{ fontSize: '0.78rem', color: 'primary.main', fontFamily: 'monospace' }}
                          >
                            #{ticket.id.toString().padStart(5, '0')}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', py: 0.6, px: 2 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', lineHeight: 1.2 }}>
                            {ticket.cliente?.nome || 'N/A'}
                          </Typography>
                          {ticket.quem_solicitou && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1 }}>
                              Solicitado por: {ticket.quem_solicitou}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', py: 0.6, px: 2 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 500 }}>
                            {ticket.assunto?.nome || 'Sem Assunto'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.6, px: 2 }}>
                          <Chip
                            label={getPrioridadeLabel(ticket.prioridade)}
                            color={prioridadeColor as any}
                            size="small"
                            sx={{ fontSize: '0.66rem', height: 20, fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 0.6, px: 2 }}>
                          <Chip
                            label={getStatusLabel(ticket.status)}
                            color={statusColor as any}
                            size="small"
                            sx={{ fontSize: '0.66rem', height: 20, fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', py: 0.6, px: 2 }}>
                          {ticket.atribuido_para_funcionario ? (
                            <Box display="flex" alignItems="center" gap={0.8}>
                              <Avatar
                                sx={{
                                  width: 24,
                                  height: 24,
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                  color: 'secondary.main'
                                }}
                              >
                                {ticket.atribuido_para_funcionario.nome.charAt(0)}
                              </Avatar>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                {ticket.atribuido_para_funcionario.nome.split(' ')[0]}
                              </Typography>
                              {ticket.atribuido_para_funcionario.coordenadas ? (
                                <Tooltip title={`Ver localização de ${ticket.atribuido_para_funcionario.nome.split(' ')[0]} no mapa`}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const coords = ticket.atribuido_para_funcionario!.coordenadas!;
                                      const [lat, lng] = coords.split(',').map(c => c.trim());
                                      window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
                                    }}
                                    sx={{
                                      p: 0.3,
                                      color: 'success.main',
                                      '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.1) },
                                    }}
                                  >
                                    <LocationOn sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                              Não atribuído
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', py: 0.6, px: 2 }}>
                          <Tooltip title={new Date(ticket.createdAt).toLocaleString()}>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 500 }}>
                                {formatDate(ticket.createdAt)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                {new Date(ticket.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', py: 0.6, px: 2 }}>
                          {ticket.data_agendamento ? (
                            <Tooltip title={new Date(ticket.data_agendamento).toLocaleString('pt-BR')}>
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 500 }}>
                                  {new Date(ticket.data_agendamento).toLocaleDateString('pt-BR')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                  {new Date(ticket.data_agendamento).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                              </Box>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.6, px: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05) }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditTicketId(ticket.id);
                                setFormOpen(true);
                              }}
                            >
                              <Edit sx={{ fontSize: 18 }} color="warning" />
                            </IconButton>
                            <IconButton
                              size="small"
                              sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTicketId(ticket.id);
                                setInfoViewOpen(true);
                              }}
                            >
                              <Visibility sx={{ fontSize: 18 }} color="primary" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 0.8,
                  px: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                  Mostrando <strong>{filteredTickets.length}</strong> chamado{filteredTickets.length !== 1 ? 's' : ''}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Total Geral: <strong>{tickets.length}</strong>
                  </Typography>
                </Box>
              </Box>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ===== ABA AGENDA ===== */}
      {activeTab === 1 && (
        <Box
          sx={{
            height: 'calc(100vh - 270px)',
            minHeight: 620,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Legenda de cores */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mr: 0.5 }}>
              Legenda:
            </Typography>
            {[
              { label: 'Urgente', color: theme.palette.error.main },
              { label: 'Aberto', color: theme.palette.warning.main },
              { label: 'Em Andamento', color: theme.palette.info.main },
              { label: 'Resolvido/Fechado', color: theme.palette.success.main },
            ].map((item) => (
              <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color, flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary">{item.label}</Typography>
              </Box>
            ))}
            <Chip
              size="small"
              label={`${calendarEvents.length} agendamento${calendarEvents.length !== 1 ? 's' : ''}`}
              sx={{ height: 20, fontSize: '0.7rem', ml: 'auto' }}
              color="primary"
              variant="outlined"
            />
          </Box>

          <Paper elevation={0} sx={{
            p: 1.5,
            flexGrow: 1,
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
          }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : (
              <DnDCalendar
                localizer={calendarLocalizer}
                events={calendarEvents}
                startAccessor="start"
                endAccessor="end"
                date={calendarDate}
                onNavigate={(date) => setCalendarDate(date)}
                view={calendarView}
                onView={(view) => {
                  setCalendarView(view);
                  if (view === Views.AGENDA) {
                    const startOfMonth = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), 1);
                    setCalendarDate(startOfMonth);
                  }
                }}
                defaultView={Views.WEEK}
                views={[Views.WEEK, Views.MONTH, Views.DAY, Views.AGENDA]}
                onEventDrop={handleEventDrop}
                resizable={false}
                selectable
                min={minCalendarTime}
                max={maxCalendarTime}
                step={30}
                timeslots={2}
                formats={calendarFormats}
                length={getDate(lastDayOfMonth(calendarDate)) - 1}
                style={{ height: '100%', width: '100%' }}
                messages={{
                  next: 'Próximo',
                  previous: 'Anterior',
                  today: 'Hoje',
                  month: 'Mês',
                  week: 'Semana',
                  day: 'Dia',
                  agenda: 'Agenda',
                  date: 'Data',
                  time: 'Hora',
                  event: 'Chamado',
                  allDay: 'Dia todo',
                  noEventsInRange: 'Nenhum chamado agendado neste período.',
                  showMore: (total) => `+${total} mais`,
                }}
                culture="pt-BR"
                onSelectEvent={(event) => {
                  setSelectedTicketId((event as ChamadoCalendarEvent).resource.id);
                  setInfoViewOpen(true);
                }}
                eventPropGetter={calendarEventStyleGetter}
                popup
                components={{
                  event: ({ event }) => {
                    const ev = event as ChamadoCalendarEvent;
                    const t = ev.resource;
                    const startTime = format(new Date(ev.start), 'HH:mm');
                    const endTime = format(new Date(ev.end), 'HH:mm');

                    // Na visualização por Mês: exibição horizontal compacta (Hora, Nome do Cliente e Assunto)
                    if (calendarView === Views.MONTH) {
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.6,
                            width: '100%',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.2,
                          }}
                        >
                          <Typography
                            component="span"
                            sx={{
                              fontSize: '0.70rem',
                              fontWeight: 800,
                              fontFamily: 'monospace',
                              opacity: 0.95,
                              flexShrink: 0,
                            }}
                          >
                            {startTime}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              flexShrink: 1,
                            }}
                          >
                            {t.cliente?.nome || 'Cliente'}
                          </Typography>
                          {t.assunto?.nome && (
                            <Typography
                              component="span"
                              sx={{
                                fontSize: '0.68rem',
                                opacity: 0.85,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                flexShrink: 2,
                              }}
                            >
                              — {t.assunto.nome}
                            </Typography>
                          )}
                        </Box>
                      );
                    }

                    // Na visualização por Semana e Dia:
                    // Linha superior: Horário à esquerda e Protocolo à direita
                    // Linha principal: Somente o Nome do Cliente
                    // Linha de baixo da principal: Técnico à esquerda (se houver) e Assunto à direita
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          p: '2px 5px',
                          overflow: 'hidden',
                          justifyContent: 'space-between',
                        }}
                      >
                        {/* Linha superior: Horário à esquerda e Protocolo à direita */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 0.8,
                            overflow: 'hidden',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.68rem',
                              fontWeight: 800,
                              fontFamily: 'monospace',
                              opacity: 0.95,
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                            }}
                          >
                            #{t.id.toString().padStart(5, '0')}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.67rem',
                              fontWeight: 600,
                              fontFamily: 'monospace',
                              whiteSpace: 'nowrap',
                              opacity: 0.9,
                              flexShrink: 0,
                            }}
                          >
                            {startTime} - {endTime}
                          </Typography>

                        </Box>

                        {/* Linha principal: Somente o Nome do Cliente */}
                        <Typography
                          sx={{
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: 1.2,
                          }}
                        >
                          {t.cliente?.nome || 'Cliente'}
                        </Typography>

                        {/* Linha de baixo da principal: Técnico à esquerda e Assunto à direita */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 0.6,
                            overflow: 'hidden',
                            width: '100%',
                          }}
                        >
                          {t.atribuido_para_funcionario?.nome && (
                            <Typography
                              sx={{
                                fontSize: '0.64rem',
                                opacity: 0.85,
                                fontStyle: 'italic',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: 1.1,
                                flexShrink: 0,
                                mr: 'auto',
                              }}
                            >
                              Téc: {t.atribuido_para_funcionario.nome.split(' ')[0]}
                            </Typography>
                          )}

                          {t.assunto?.nome && (
                            <Typography
                              sx={{
                                fontSize: '0.68rem',
                                fontWeight: 600,
                                opacity: 0.95,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                textAlign: 'right',
                                lineHeight: 1.1,
                                ml: 'auto',
                              }}
                            >
                              {t.assunto.nome}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  },
                  agenda: {
                    date: ({ label, day }: any) => {
                      const { text, color } = formatAgendaDateLabel(day, label);
                      return (
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color, whiteSpace: 'nowrap', fontSize: '0.82rem' }}
                        >
                          {text}
                        </Typography>
                      );
                    },
                    event: ({ event }) => {
                      const color = getCalendarEventColor(event as ChamadoCalendarEvent);
                      const ev = event as ChamadoCalendarEvent;
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, bgcolor: color }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{ev.title}</Typography>
                        </Box>
                      );
                    },
                  },
                }}
              />
            )}
          </Paper>
        </Box>
      )}

      {/* ===== ABA RANKING ===== */}
      {activeTab === 2 && <ChamadosRankingTab />}

      {activeTab === 3 && <ChamadosRankingTecnicosTab />}

      {activeTab === 4 && <ChamadosRankingAssuntosTab />}

      <style>{getBigCalendarStyles(theme)}</style>

      {/* Modal de Novo/Editar Chamado */}
      <ChamadoForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditTicketId(null);
        }}
        onSuccess={handleFormSuccess}
        ticketId={editTicketId}
      />

      {/* Modal de Visualização de Detalhes */}
      <ChamadosInfoView
        open={infoViewOpen}
        onClose={() => {
          setInfoViewOpen(false);
          setSelectedTicketId(null);
        }}
        ticketId={selectedTicketId}
        onSuccess={() => {
          loadTickets(); // Recarregar tickets após fechar chamado
        }}
      />
    </Box>
  );
};

export default ChamadosPage; 